import {
	ActionBar,
	Checkbox,
	Input,
	SpinnerWithBackground,
	Text,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useCycleStore } from '../store'
import type { CycleGeneralTabInput } from './types'

export const CycleGeneralTab = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const { cycle, useCreateCycle, useEditCycle } = useCycleStore()
	const { createCycle, isLoading: isCreating } = useCreateCycle()
	const { editCycle, isLoading: isEditing } = useEditCycle(cycle?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isValid, isDirty },
		reset,
		getValues,
		trigger,
	} = useForm<CycleGeneralTabInput>({
		mode: 'onChange',
		defaultValues: {
			name: cycle?.name || '',
			launchDate: cycle?.launchDate || '',
			previewDate: cycle?.previewDate || '',
			deadline: cycle?.deadline || '',
			isTemplate: cycle?.isTemplate || false,
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (cycle) {
			reset({
				name: cycle.name || '',
				launchDate: cycle.launchDate || '',
				previewDate: cycle.previewDate || '',
				deadline: cycle.deadline || '',
				isTemplate: cycle.isTemplate || false,
			})
		}
	}, [cycle, reset])

	const handleSubmitInternal = async (data: Partial<CycleGeneralTabInput>) => {
		if (!cycle) return
		if (!data.name) return

		if (cycle.id) {
			await editCycle({
				name: data.name,
				launchDate: data.launchDate || '',
				previewDate: data.previewDate || '',
				deadline: data.deadline || null,
				isTemplate: data.isTemplate,
			})
		} else {
			await createCycle({
				name: data.name,
				launchDate: data.launchDate || '',
				previewDate: data.previewDate || '',
				deadline: data.deadline || null,
				isTemplate: data.isTemplate,
			})
		}
		reset(data as CycleGeneralTabInput)
	}

	const isPending = isCreating || isEditing

	return (
		<form
			onSubmit={handleSubmit((data) => handleSubmitInternal(data as CycleGeneralTabInput))}
			className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 md:grid-cols-4 gap-x-6 xl:gap-x-12 gap-6 px-6 items-start'>
				<div>
					<Controller
						name='name'
						control={control}
						rules={{
							required: 'Name is required',
						}}
						render={({ field }) => (
							<Input
								data-testid='cycle-name-input'
								label='Name'
								placeholder='Name'
								variant={errors.name ? 'error' : 'default'}
								hint={errors.name?.message as string}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='previewDate'
						control={control}
						rules={{
							validate: (value) => {
								const launchDate = getValues('launchDate')

								if (value && launchDate && value >= launchDate) {
									return 'Preview date must be before the launch date'
								}

								return true
							},
						}}
						render={({ field }) => (
							<Input
								data-testid='cycle-preview-date'
								label='Preview Date'
								type='date'
								variant={errors.previewDate ? 'error' : 'default'}
								hint={errors.previewDate?.message as string}
								value={field.value || ''}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='launchDate'
						control={control}
						render={({ field }) => (
							<Input
								data-testid='cycle-launch-date'
								label='Launch Date'
								type='date'
								value={field.value || ''}
								onChange={(e) => {
									field.onChange(e)
									trigger('previewDate')
								}}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='deadline'
						control={control}
						render={({ field }) => (
							<Input
								data-testid='cycle-deadline'
								label='Deadline'
								type='date'
								value={field.value || ''}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>
			</div>
			<div className='px-6 mt-6'>
				<Controller
					name='isTemplate'
					control={control}
					render={({ field }) => (
						<div className='flex flex-col gap-1'>
							<Checkbox
								data-testid='cycle-is-template'
								label='Template Cycle'
								checked={field.value || false}
								onChange={(e) => field.onChange(e.target.checked)}
							/>
							<Text variant='xsmall' className='text-gray-600 ml-6'>
								Template cycles can be configured without participants and cannot be launched directly
							</Text>
						</div>
					)}
				/>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate(`/organizations/${organizationId}/cycles`),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						disabled: !isValid,
						onClick: handleSubmit((data) => handleSubmitInternal(data as CycleGeneralTabInput)),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
